import React, { useState } from 'react';
import { Col, Form } from 'antd';
import { message } from 'antd';
import { I18n } from '@aws-amplify/core';
import { useQueryClient } from '@tanstack/react-query';
import BrowserImageCompression from 'browser-image-compression';

import { CustomImage } from '../CustomImage';
import { Button as UploadFileButton } from '../Button';
import { useDestroyAttachment } from '@/components/ActionPlan/UploadFile/hooks/useDestroyAttachment';
import { useCreateAttachment } from '@/hooks/useCreateAttachment';
import { useUploadFileSignedUrl } from '@/components/ActionPlan/UploadFile/hooks/useUploadFileSignedUrl';
import { useUploadAttachment } from '@/components/ActionPlan/UploadFile/hooks/useUploadAttachment';
import { useApplicationContext } from '@/context/v1/Application/context';
import { QUERY_KEYS } from '@/utils/query-keys';
import * as S from './styles';

const MAX_UPLOAD_FILE_SIZE = 10 * 1024 * 1024;

interface AttachmentFile {
	id: string;
	description: string;
	generated_name: string;
	original_name: string;
	src: string;
	size: number;
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
	table_origin: string;
	is_temporary?: boolean;
}

interface FilesProps {
	origin_table: string;
	table_origin_id: string;
	value?: AttachmentFile[];
}

export function Files({ origin_table, table_origin_id, value = [] }: Readonly<FilesProps>): JSX.Element {
	const [isUploading, setIsUploading] = useState<boolean>(false);

	const form = Form.useFormInstance();
	const queryClient = useQueryClient();
	const { organization, company } = useApplicationContext();
	const { mutateAsync: createAttachment } = useCreateAttachment();
	const { mutateAsync: uploadFileSignedUrl } = useUploadFileSignedUrl();
	const { mutateAsync: uploadAttachmentUrl } = useUploadAttachment();
	const { mutateAsync: destroyAttachment } = useDestroyAttachment();

	function isImageListNotFull(): boolean {
		return true;
	}

	async function handleCompressImage(file: File): Promise<File> {
		const isImage = file instanceof File && file.type.includes('image');
		const attachment = isImage ? await BrowserImageCompression(file, {}) : (file as File);

		if (attachment.size > MAX_UPLOAD_FILE_SIZE) {
			throw new Error('Invalid file size. Please upload files up to 10MB.');
		}

		return attachment;
	}

	async function handleCreateUrlSigned(attachment: File): Promise<any> {
		const payload = {
			organization_id: organization.id!,
			company_id: company.id!,
			size: attachment.size,
			file_name: attachment.name,
			content_type: attachment.type,
			action_plan_id: undefined
		};
		const result = await uploadAttachmentUrl(payload);
		return result;
	}

	async function handleUploadAttachment(options: any): Promise<void> {
		setIsUploading(true);
		try {
			const attachment = await handleCompressImage(options.file);
			const { key, url, original_name } = await handleCreateUrlSigned(attachment);

			await uploadFileSignedUrl({ url, file: attachment, contentType: attachment.type });

			const result = await createAttachment({
				organization_id: organization.id!,
				company_id: company.id!,
				origin_table,
				table_origin_id,
				file: {
					generated_name: key,
					size: attachment.size,
					original_name: original_name
				}
			});

			const currentAttachments = form.getFieldValue('attachments') || [];
			form.setFieldsValue({
				attachments: [
					...currentAttachments,
					{
						...result?.data,
						generated_name: key,
						original_name,
						size: attachment.size,
						src: URL.createObjectURL(options.file),
						table_origin: origin_table,
						is_temporary: true
					}
				]
			});

			await queryClient.invalidateQueries([QUERY_KEYS.GET_ATTACHMENT_LIST]);
		} catch (error: any) {
			message.error(I18n.get(error.message));
		} finally {
			setIsUploading(false);
		}
	}

	async function handleRemoveAttachment(attachment: AttachmentFile): Promise<void> {
		if (!attachment.is_temporary) {
			await destroyAttachment({
				organization_id: organization.id!,
				company_id: company.id!,
				id: attachment.id
			});

			return;
		}

		const currentAttachments = form.getFieldValue('attachments') || [];
		form.setFieldsValue({
			attachments: currentAttachments.filter((file: any) => file.id !== attachment.id)
		});
	}

	return (
		<S.List>
			{value.map((file) => {
				const handleDelete = () => handleRemoveAttachment(file);

				return (
					<CustomImage
						key={file.id}
						image={file}
						onDelete={handleDelete}
					/>
				);
			})}
			{isUploading && (
				<Col>
					<S.ImageSkeleton active />
				</Col>
			)}
			<Col>
				<S.CustomUpload
					multiple={true}
					showUploadList={false}
					listType="picture-card"
					accept=".jpg, .jpeg, .png"
					customRequest={handleUploadAttachment}
				>
					{isImageListNotFull() && !isUploading && <UploadFileButton />}
				</S.CustomUpload>
			</Col>
		</S.List>
	);
}
